'use client';

import { useSelectDialog } from '@/components/common/SelectDialog/useSelectDialog';
import { useTranslations } from 'next-intl';
import { JobsRouteQuery } from './gen/graphql/JobsRoute.generated';
import { useI18nLabel } from '@/hooks/useI18nLabel';
import { useRouter, usePathname } from '@/i18n/navigation';
import { Breadcrumbs } from '@/components/common/Breadcrumbs/Breadcrumbs';
import { FilterCondition, FilterConditionItem } from './components/FilterCondition';
import { useSelectPrefectureDialog } from '@/components/features/SelectPrefectureDialog/useSelectPrefectureDialog';
import { JobList } from './components/JobList';
import { useJobCategoryOptions } from '@/hooks/useJobCategoryOptions';
import { useJobBenefitOptions } from '@/hooks/useJobBenefitOptions';
import { usePrefectureOptions } from '@/hooks/usePrefectureOptions';
import { useFormatTitle } from './hooks/useFormatTitle';
import { useBreadcrumbs } from './hooks/useBreadcrumbs';
import { PrefectureValue } from '@/constants/prefectures';
import { QueryParams } from './types/QueryParams';
import { QueryRef } from '@apollo/client/react/types/types';
import { useReadQuery } from '@apollo/client/react/hooks/useReadQuery';
import { Callout } from '@/components/common/Callout/Callout';
import { EmptyState } from './components/EmptyState';
import { JobListLoadingState } from './components/JobListLoadingState';
import { LogScreenDisplay } from '@/libs/clientEvents/LogScreenDisplay';
import { useTransition } from 'react';
import { makeTopDeepLink } from '@/utils/deeplink';
import { AppDownloadBanner } from '@/components/common/AppDownloadBanner/AppDownloadBanner';
import { LightbulbIcon } from '@/components/icons/gen';

export interface JobsRouteProps {
  queryRef: QueryRef<JobsRouteQuery>;
  jobCategoryId: string | undefined;
  jobBenefitId: string | undefined;
  prefCode: PrefectureValue | undefined;
}

export function JobsRoute({ queryRef, jobBenefitId, jobCategoryId, prefCode }: JobsRouteProps) {
  const {
    data: { jobBenefits, jobCrewCategoryMasters, fastTimelineJobs },
  } = useReadQuery(queryRef);

  const t = useTranslations();
  const translate = useI18nLabel();
  const router = useRouter();
  const pathname = usePathname();

  const params: QueryParams = {
    ...(jobBenefitId !== undefined && { job_benefit: jobBenefitId }),
    ...(jobCategoryId !== undefined && { job_category: jobCategoryId }),
    ...(prefCode !== undefined && { prefecture: prefCode }),
  };

  // Make options
  const { makeOptions: makeJobCategoryOptions } = useJobCategoryOptions();
  const { makeOptions: makeJobBenefitOptions } = useJobBenefitOptions();
  const { prefectureOptions } = usePrefectureOptions();

  // Find selected options
  const prefecture = prefectureOptions.find((prefecture) => prefecture.value === prefCode);
  const jobBenefit = jobBenefits.find((jobBenefit) => jobBenefit.id === jobBenefitId);
  const jobCategory = jobCrewCategoryMasters.find(
    (jobCategory) => jobCategory.id === jobCategoryId
  );

  // Format title and breadcrumbs
  const title = useFormatTitle({ prefecture, jobCategory, jobBenefit });
  const breadcrumbs = useBreadcrumbs({ prefecture, jobCategory, jobBenefit });

  const [isPending, startTransition] = useTransition();

  const jobCategoryDialog = useSelectDialog({
    title: t('jobs.jobCategory.dialogTitle'),
    options: makeJobCategoryOptions(jobCrewCategoryMasters),
    value: jobCategory?.id,
    onValueChange: (value) => {
      const newParams = { ...params };
      if (value) {
        newParams.job_category = value;
      } else {
        delete newParams.job_category;
      }
      startTransition(() => {
        router.push({
          pathname,
          query: newParams,
        });
      });
    },
  });

  const jobBenefitDialog = useSelectDialog({
    title: t('jobs.jobBenefit.dialogTitle'),
    options: makeJobBenefitOptions(jobBenefits),
    value: jobBenefit?.id,
    onValueChange: (value) => {
      const newParams = { ...params };
      if (value) {
        newParams.job_benefit = value;
      } else {
        delete newParams.job_benefit;
      }
      startTransition(() => {
        router.push({
          pathname,
          query: newParams,
        });
      });
    },
  });

  const prefectureDialog = useSelectPrefectureDialog({
    value: prefCode,
    onValueChange: (value) => {
      const newParams = { ...params };
      if (value) {
        newParams.prefecture = value;
      } else {
        delete newParams.prefecture;
      }
      startTransition(() => {
        router.push({
          pathname,
          query: newParams,
        });
      });
    },
  });

  const handleChangePrefecture = () => {
    prefectureDialog.onOpenChange(true);
  };

  const handleChangeJobCategory = () => {
    jobCategoryDialog.onOpenChange(true);
  };

  const handleChangeJobBenefit = () => {
    jobBenefitDialog.onOpenChange(true);
  };

  return (
    <>
      <LogScreenDisplay location="worker_web_jobs" />
      <div className="container mx-auto px-6 pt-6 pb-10 flex flex-col items-center gap-10">
        <div className="flex flex-col gap-4 w-full">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <h1 className="text-x-large font-bold">{title}</h1>

          <FilterCondition>
            <FilterConditionItem
              label={t('jobs.prefecture.label')}
              value={prefecture?.label}
              onClickChange={handleChangePrefecture}
            />
            <FilterConditionItem
              label={t('jobs.jobCategory.label')}
              value={jobCategory ? translate(jobCategory) : undefined}
              onClickChange={handleChangeJobCategory}
            />
            <FilterConditionItem
              label={t('jobs.jobBenefit.label')}
              value={jobBenefit ? translate(jobBenefit) : undefined}
              onClickChange={handleChangeJobBenefit}
            />
          </FilterCondition>
        </div>

        {fastTimelineJobs.edges?.length === 0 ? (
          <EmptyState prefecture={prefecture} jobCategory={jobCategory} jobBenefit={jobBenefit} />
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <Callout Icon={LightbulbIcon} variant="primary">
              {t('jobs.callout.description')}
            </Callout>
            {isPending ? (
              <JobListLoadingState />
            ) : (
              <JobList
                prefCode={prefCode}
                jobCrewCategoryId={jobCategoryId}
                jobBenefitId={jobBenefitId}
              />
            )}
          </div>
        )}
        <AppDownloadBanner href={makeTopDeepLink()} className="hidden tab:flex pc:flex" />

        {/* Use Dialog properties from all hooks */}
        {prefectureDialog.Dialog}
        {jobCategoryDialog.Dialog}
        {jobBenefitDialog.Dialog}
      </div>
    </>
  );
}
